import React from 'react';

import { AppConfig } from '../core-bundle/config/app-config';
import {
  FF_DISABLE_SPLASHPAGE,
  FF_RECIPIENT_SPLASH_PAGE,
  FF_FREE_ACCOUNT_SPLASH_EXPERIMENT,
  EXP_FREE_ACCOUNT_SPLASH,
} from '@wetransfer/transfer/constant/experiments';
import ExperimentDiscountModal from '@wetransfer/transfer/constant/experiments/discount-modal';
import { UNLIMITED_TRANSFER_LIMIT } from '@wetransfer/transfer/constant/sizes';
import {
  PLUS_ACCOUNT,
  FREE,
} from '@wetransfer/transfer/constant/subscriptions';
import Helper from '@wetransfer/transfer/helper';
import Analytics from '@wetransfer/transfer/modules/analytics';
import Browser from '@wetransfer/transfer/modules/concorde-browser';
import Cookie from '@wetransfer/transfer/modules/concorde-cookie';
import { getVariant } from '@wetransfer/transfer/modules/experiments';
import I18n from '@wetransfer/transfer/modules/i18n';
import TermsAndConditions from '@wetransfer/transfer/modules/terms-and-conditions';
import Store from '@wetransfer/transfer/store';

import { getProTransferLimit } from './subscriptions';

// Write cookie with date and amount of transfers sent.
function writeCookie() {
  const now = new Date();

  const formatted = `${
    now.getMonth() + 1
  }/${now.getDate()}/${now.getFullYear()}`;
  const sent = Helper.getWTSent();
  const cookieValue = [formatted, sent].join(',');
  Cookie.set(AppConfig.cookies.takeover.name, cookieValue, { days: 365 });
}

function readCookie() {
  let cookie = Cookie.get(AppConfig.cookies.takeover.name);

  if (!cookie) {
    return false;
  }

  // Examine cookie
  cookie = cookie.split(',');
  const [date, filesSent] = cookie;

  if (!date || !filesSent) {
    return false;
  }
  const sent = parseInt(filesSent || 0, 10);

  return { date, sent };
}

function hasValidCookie() {
  const previously = readCookie();

  if (!previously) {
    return false;
  }

  // When was this set?
  const daysSince = Helper.getDaysBetweenDates(previously.date, new Date());

  // Check if daysSince is a number
  if (isNaN(daysSince)) {
    return false;
  }

  // How many transfers have been sent?
  const sent = Helper.getWTSent();
  // How many transfers have been done since?
  const sentSince = sent - previously.sent;

  // Don't show if less than 7 days have expired and less than 20 transfers have been done
  if (daysSince < 7 && sentSince < 20) {
    return true;
  }

  return false;
}

// This diagram is out of date. But we can keep it for future reference when we refactor this page.
// https://code2flow.com/D0hxyC : contains all the logic to show/not show the Splashpage (out of date)
function shouldDisplaySplashpage() {
  const isVisitingFirstTime = Helper.getFirstVisitDate() === undefined;
  const { route, channel, auth, user } = Store.getState();

  let isOnExcludedRoute =
    route.path.includes('/wallpaper/') || route.path.includes('/downloads/');

  if (getVariant(FF_RECIPIENT_SPLASH_PAGE) === 'on') {
    isOnExcludedRoute = route.path.includes('/wallpaper/');
  }

  if (isVisitingFirstTime) {
    return false;
  }

  if (isOnExcludedRoute) {
    return false;
  }

  if (route.type !== 'transfer' && route.name !== 'home-mobile') {
    return false;
  }

  // Not in IE9 or lower
  if (Browser.matches('explorer <= 9.0')) {
    return false;
  }

  // Not for loggedin SSO users
  if (
    !auth.isInitialized ||
    (auth.isInitialized && auth.isLoggedIn && user.planTier !== 'free')
  ) {
    return false;
  }

  // Not when T&C are not accepted
  if (
    !TermsAndConditions.areAccepted() &&
    getVariant(FF_RECIPIENT_SPLASH_PAGE) !== 'on'
  ) {
    return false;
  }

  // Don't show on channels
  if (channel && channel.name) {
    return false;
  }

  // Verify existing cookie (if any)
  if (hasValidCookie()) {
    return false;
  }

  if (getVariant(FF_DISABLE_SPLASHPAGE) === 'on') {
    return false;
  }

  const isNotFreeAccountExperiment =
    getVariant(FF_FREE_ACCOUNT_SPLASH_EXPERIMENT) !== 'on' ||
    !getVariant(
      EXP_FREE_ACCOUNT_SPLASH.key,
      EXP_FREE_ACCOUNT_SPLASH.default
    ) === EXP_FREE_ACCOUNT_SPLASH.showPremiumCard;

  if (user.planTier === 'free' && isNotFreeAccountExperiment) {
    return false;
  }

  if (Cookie.get(AppConfig.cookies.discountModalSeen.name)) {
    return false;
  }

  if (
    !ExperimentDiscountModal.payload &&
    ExperimentDiscountModal.isShowDiscountModal()
  ) {
    return false;
  }

  return true;
}

const trackFunnel = (action, secondary, label) => {
  Analytics.trackSnowplowEvent({
    category: 'splashscreen',
    action,
    label,
  });

  if (secondary) {
    Analytics.trackWithPixelOrSDK(secondary);
  }
};

const makeFreeAccountFeatureList = () => {
  return (
    <>
      <li className="plan__feature--unavailable">
        {I18n.t('splash.plan.free_account.feature_1')}
      </li>
      <li>
        {I18n.t('splash.plan.free_account.feature_2', {
          free_transfer_limit: FREE.TRANSFER_LIMIT,
        })}
      </li>
      <li>{I18n.t('splash.plan.free_account.feature_3')}</li>
    </>
  );
};

const makeProFeatureList = () => {
  const proTransferLimit = getProTransferLimit();

  const feature2 =
    proTransferLimit === UNLIMITED_TRANSFER_LIMIT
      ? 'splash.plan.pro.feature_2_unlimited'
      : 'splash.plan.pro.feature_2';

  return (
    <>
      <li>
        {I18n.t('splash.plan.pro.feature_1', {
          storage_limit_pro: PLUS_ACCOUNT.STORAGE_LIMIT,
        })}
      </li>
      <li>
        {I18n.t(feature2, {
          transfer_limit_pro: proTransferLimit,
        })}
      </li>
      <li>{I18n.t('splash.plan.pro.feature_3')}</li>
      <li>{I18n.t('splash.plan.pro.feature_4')}</li>
      <li>{I18n.t('splash.plan.pro.feature_5')}</li>
    </>
  );
};

const content = (key) => {
  if (typeof key !== 'string') {
    return '';
  }

  if (key.length === 0) {
    return '';
  }

  return I18n.t(key);
};

export default {
  shouldDisplaySplashpage,
  writeCookie,
  trackFunnel,
  makeFreeAccountFeatureList,
  makeProFeatureList,
  content,
};
